import { type Fill, type Borders, Workbook } from 'exceljs';
import { isNaN, isNil, isObject } from 'lodash-es';
import BigNumber from 'bignumber.js';
import type { CustomColumn } from '@/common/utils/types';
import {
  convertBytesToUnits,
  convertMsToSec,
  fileDownload,
  getFormattedTzTimeNow,
  roundToDigitNumber,
} from '@/common/utils/commonUtils';
import type { ExportExcelOption } from './excel.types';
import { colorStringToHex, mergeColorToHex } from '../colorConvertUtil';

// ExcelJS는 CSS 변수(var(--xxx))를 해석하지 못하므로, export 경로에서만 :root 기준으로 hex 리터럴로 풀어줌
// 해석 실패 시 undefined 반환 → 셀 fill 미적용
const resolveCssVar = (
  color?: string,
  depth = 0,
  styles = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : undefined,
): string | undefined => {
  if (!color || !styles) {
    return color;
  }
  if (depth > 10) {
    return undefined;
  }
  const trimmed = color.trim();
  if (!trimmed.startsWith('var(')) {
    return color;
  }
  // var(--name) 또는 var(--name, fallback) 분해
  const match = /^var\(\s*(--[\w-]+)\s*(?:,\s*(.+))?\s*\)$/.exec(trimmed);
  if (!match) {
    return undefined;
  }
  const [, varName, fallback] = match;
  const resolved = styles.getPropertyValue(varName).trim();
  if (resolved) {
    return resolveCssVar(resolved, depth + 1, styles);
  }
  if (fallback) {
    return resolveCssVar(fallback.trim(), depth + 1, styles);
  }
  return undefined;
};

const convertRgbaToHex = (rgba?: string | null) => {
  if (isNil(rgba)) {
    return undefined;
  }

  const hex = colorStringToHex(rgba, true).slice(1);
  if (hex.length === 8) {
    return `FF${hex.slice(0, 6)}`;
  }
  return `FF${hex}`;
};

const mergeColorAndConvert = (baseColor?: string, altColor?: string) =>
  convertRgbaToHex(mergeColorToHex(resolveCssVar(baseColor), resolveCssVar(altColor)));

export const formatRowDataIfObjectOrArray = (rowData: any) => {
  if (isNil(rowData)) {
    return '';
  }
  if (rowData instanceof BigNumber) {
    return rowData.toString();
  }
  if (rowData instanceof Array) {
    return rowData.join(', ');
    // NOTE: 이 줄에서 버그 발생할 경우, rowData 가 객체형 배열이기 때문에 join이 불가능한 것
    // NOTE: 각 BaseGrid exportExcelOption에서 적절한 rowDataFormatter 지정 필요
    // NOTE: 사용자태그 컬럼은 기본적으로 hidden 이기 때문에 해당 함수에 진입 X
  }
  if (isObject(rowData)) {
    return Object.keys(rowData).join(', ');
  }
  return `${rowData}`;
};

export const formatRowData = (rowData: any, columnInfo: Partial<CustomColumn>) => {
  if (!columnInfo.type) {
    return formatRowDataIfObjectOrArray(rowData);
  }
  const { type } = columnInfo;

  let value = rowData;
  switch (type) {
    case 'float':
      if (value === 0) {
        return 0;
      }
      if (isNil(value)) {
        return '';
      }
      return value;
    case 'stringNumber':
    case 'number':
      if (typeof value === 'string') {
        if (value.trim() === '') {
          return '';
        }
        if (isNaN(+value)) {
          return value;
        }

        value = +value;
      }
      if (value === 0) {
        return 0;
      }
      if (isNil(value) || isNaN(value)) {
        return '';
      }
      return value;
    case 'string':
    case 'boolean':
    default:
      return formatRowDataIfObjectOrArray(value);
  }
};

/**
 * 소수점 처리 + 천자리 콤마 제거
 * rendererType='digit' 처리용
 */
export const formatDecimal = (value: any, decimal = 3): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    if (value.trim() === '') {
      return null;
    }
    const parsedValue = parseFloat(value.replace(/,/g, ''));
    return roundToDigitNumber(parsedValue, decimal);
  }
  return roundToDigitNumber(value, decimal);
};

/**
 * 바이트 단위 변환
 * rendererType='byte' 처리용
 */
export const formatByte = (value: any, decimal = 3): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  return convertBytesToUnits(value, decimal);
};

export const styleExcelFile = (
  workbook: Workbook,
  option: ExportExcelOption,
  headerRowLength = 1,
) => {
  const worksheet = workbook.getWorksheet(1);
  if (!worksheet) {
    return;
  }

  const { colorStyle } = option;
  const { cellStyle, rowStyle } = colorStyle || {};
  const commonFill: Fill = {
    type: 'pattern',
    pattern: 'solid',
  };
  const BORDER_RGBA = 'FFD9D9D9';
  const border: Partial<Borders> = {
    top: { style: 'thin', color: { argb: BORDER_RGBA } },
    left: { style: 'thin', color: { argb: BORDER_RGBA } },
    bottom: { style: 'thin', color: { argb: BORDER_RGBA } },
    right: { style: 'thin', color: { argb: BORDER_RGBA } },
  };

  // All Cells
  worksheet.eachRow((row, index) => {
    row.height = 24;
    const rowNumber = index - (headerRowLength + 1);

    const rowBgColor = rowStyle?.[rowNumber]?.bgColor;
    const rowFontColor = rowStyle?.[rowNumber]?.fontColor;
    row.eachCell((cell, colNumber) => {
      const colField = worksheet.getColumn(colNumber).key || '';
      const cellBgColor = cellStyle?.[colField]?.[rowNumber]?.bgColor;
      const cellFontColor = cellStyle?.[colField]?.[rowNumber]?.fontColor;
      const bgColor = mergeColorAndConvert(rowBgColor, cellBgColor);
      const fontColor = mergeColorAndConvert(rowFontColor, cellFontColor);

      cell.fill = {
        ...commonFill,
        fgColor: { argb: bgColor },
      };
      cell.font = {
        color: { argb: fontColor },
      };
      cell.border = border;
    });
  });

  // Top Column
  for (let ix = 1; ix <= headerRowLength; ix++) {
    const headerRow = worksheet.getRow(ix);
    headerRow.height = 36;
    headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    headerRow.eachCell((cell) => {
      cell.font = {
        size: 12,
        bold: true,
      };
      cell.border = border;
    });
  }
};

export const formatExcelSecFields = ({
  fields,
  toggle: _, // DSP-21544 머지후 삭제예정
}: {
  fields: string[];
  toggle?: string;
}) =>
  fields.reduce((acc, field) => {
    acc[field] = (data: number | string) => convertMsToSec(data, { digits: 3 });
    return acc;
  }, {});

export const formatExcelNumberFields = ({ fields }: { fields: string[] }) =>
  fields.reduce((acc, field) => {
    acc[field] = (data: number) => roundToDigitNumber(data, 3);
    return acc;
  }, {});

export const getReportColumnList = (
  columns: Readonly<CustomColumn[]>,
  fieldMapper?: (column: CustomColumn) => string | undefined,
): string[] =>
  columns.reduce<string[]>((acc, column) => {
    if (column.hide || column.hiddenDisplay) {
      return acc;
    }

    const field = fieldMapper?.(column) ?? column.field;
    if (field) {
      acc.push(field);
    }

    return acc;
  }, []);

export const getLevelSymbol = (
  currentLevel: number,
  hasChildren: boolean,
  isSiblingWithChildrenAppearedBefore: boolean,
): string => {
  if (currentLevel === 0 && !hasChildren) {
    return '';
  }

  const depthSpace = '  '.repeat(currentLevel);
  const symbol = hasChildren || isSiblingWithChildrenAppearedBefore ? '▼' : '└';
  return depthSpace + symbol;
};

export const getFileName = (title: string) => {
  return title
    .replaceAll(/[^a-zA-Z0-9ㄱ-ㅎㅏ-ㅣ가-힣-_]/g, ' ')
    .replaceAll(/\s{2,}/g, ' ')
    .trim();
};

export const exportExcelFile = async (
  workbook: Workbook,
  option: ExportExcelOption = {},
  headerRowLength = 1,
) => {
  styleExcelFile(workbook, option, headerRowLength);
  await new Promise((resolve) => setTimeout(resolve, 500));
  const buffer = await workbook.xlsx.writeBuffer();
  const title = getFileName(option.title ?? 'export');
  const filename = `${title}_${getFormattedTzTimeNow('YYYY-MM-DD HH:mm:ss')}.xlsx`;
  fileDownload({ fileName: filename, fileData: buffer, fileType: 'excel' });
};
